import React from 'react';
import { ChevronDown, X, Search } from 'lucide-react';
import { useAutocomplete, type ComboboxOption } from '../../hooks/useAutocomplete';

interface ComboboxProps {
    options: ComboboxOption[];
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
}

export const Combobox: React.FC<ComboboxProps> = ({
    options,
    value,
    onChange,
    placeholder = 'Select item...',
    className = '',
    disabled = false,
}) => {
    const {
        isOpen,
        inputValue,
        highlightedIndex,
        setHighlightedIndex,
        filteredOptions,
        selectedOption,
        containerRef,
        inputRef,
        listRef,
        handleInputFocus,
        handleInputChange,
        handleOptionSelect,
        handleKeyDown,
        clearSelection,
        toggleOpen
    } = useAutocomplete({
        options,
        value,
        onChange,
        disabled,
        enforceSelection: true
    });

    return (
        <div className={`combobox-container ${className}`} ref={containerRef}>
            <div
                className={`combobox-input-wrapper ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
                onClick={() => {
                    if (!isOpen) inputRef.current?.focus();
                }}
            >
                {selectedOption && inputValue === selectedOption.label && selectedOption.icon && (
                    <div className="combobox-icon">{selectedOption.icon}</div>
                )}
                {!selectedOption && !inputValue && <div className="combobox-icon"><Search size={16} /></div>}

                <input
                    ref={inputRef}
                    type="text"
                    className="combobox-input"
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    disabled={disabled}
                />

                <div className="combobox-actions">
                    {inputValue && (
                        <div className="combobox-clear" onClick={clearSelection}>
                            <X size={14} />
                        </div>
                    )}
                    <div className="combobox-chevron" onClick={toggleOpen}>
                        <ChevronDown size={16} />
                    </div>
                </div>
            </div>

            {isOpen && !disabled && (
                <div className="combobox-menu custom-scrollbar" ref={listRef}>
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((option, index) => (
                            <div
                                key={option.value}
                                className={`combobox-option ${index === highlightedIndex ? 'highlighted' : ''} ${option.value === value ? 'selected' : ''}`}
                                onMouseDown={(e) => {
                                    e.preventDefault(); // Prevent blur
                                    handleOptionSelect(option);
                                }}
                                onMouseEnter={() => setHighlightedIndex(index)}
                            >
                                {option.icon && <div className="combobox-option-icon">{option.icon}</div>}
                                {option.label}
                            </div>
                        ))
                    ) : (
                        <div className="combobox-empty">No item found</div>
                    )}
                </div>
            )}
        </div>
    );
};
